<script setup lang="ts">
import {inject, ref} from "vue";
import {useRoute} from "vue-router";
import {FilterMatchMode} from "@primevue/core/api";
import Str from "@/Utils/Str";
import {getQuestions as apiGetQuestions} from "@/Services/Admin/admin-questions";
import {Button, Column, DataTable, MultiSelect, Tag} from "@/Components/Library";
import {PageHeading, TableRecordActions} from "@/Components/Global";
import type dayjs from "dayjs";
import type {AdminIndex} from "@/Types";

type QuestionLocal = any;

const currentRoute = useRoute();
const $date = inject("date") as typeof dayjs;
const questions = ref<AdminIndex<QuestionLocal>>();

const filters = ref({
    type: {
        value: [],
        matchMode: FilterMatchMode.IN,
    },
});

const getQuestions = async () =>
    apiGetQuestions({
        include: ["responses", "responsesCount", "optionsCount"],
        ...currentRoute.query,
    }).then((data) => (questions.value = data));
getQuestions();
</script>
<template>
    <div>
        <div class="m-container">
            <PageHeading title="Questions">
                <RouterLink class="ml-auto focus-visible:outline-none" :to="{name: 'admin.questions.create'}">
                    <Button
                        rounded
                        class="bg-askr-green text-sm text-white hover:brightness-110"
                        :icon="{
                            icon: 'ic:round-plus',
                            width: '24',
                        }"
                    >
                        <span class="hidden md:inline">Add Question</span>
                    </Button>
                </RouterLink>
            </PageHeading>
        </div>
        <DataTable
            :data="questions"
            dataKey="id"
            :loading="false"
            :filters="filters"
            @filter="getQuestions"
            defaultSort="-created_at"
        >
            <Column expander style="width: 0" />
            <Column field="id" header="ID" :sortable="true">
                <template #body="{data}">
                    <RouterLink
                        :to="{
                            name: 'admin.questions.show',
                            params: {question: data.id},
                        }"
                        class="hover:underline"
                    >
                        {{ data.id }}
                    </RouterLink>
                </template>
            </Column>
            <Column field="title" header="Question" :sortable="true">
                <template #body="{data}">
                    <RouterLink
                        :to="{
                            name: 'admin.questions.show',
                            params: {question: data.id},
                        }"
                        class="text-xs hover:underline"
                    >
                        {{ Str.of(data.title).truncate(200) }}
                    </RouterLink>
                </template>
            </Column>
            <Column field="type" header="Type" :sortable="true" :showFilterMatchModes="false" filter-field="type">
                <template #body="{data}">
                    <Tag :value="data.type.name" :class="`bg-${data.type.color}`" />
                </template>
                <template #filter="{filterModel, filterCallback}">
                    <MultiSelect
                        v-model="filterModel.value"
                        @change="filterCallback"
                        :options="['word', 'text', 'multiple_choice', 'ranking', 'slider', 'weighting', 'voice']"
                        placeholder="Select actions"
                        filter
                        class="p-column-filter"
                        style="min-width: 14rem"
                        :maxSelectedLabels="1"
                    >
                        <template #option="slotProps">
                            <div class="align-items-center flex">
                                <span>{{ Str.of(slotProps.option).title() }}</span>
                            </div>
                        </template>
                    </MultiSelect>
                </template>
                <template #filterclear="{filterCallback}" />
                <template #filterapply="{filterCallback}" />
            </Column>
            <Column field="responses_count" header="Responses" style="width: 0" :sortable="true" />
            <Column field="options_count" header="Options" style="width: 0" :sortable="true" />
            <Column field="created_at" header="Created At" data-type="date" :sortable="true">
                <template #body="{data}">
                    {{ $date(data.created_at).format("DD/MM/YYYY HH:mm") }}
                </template>
            </Column>
            <Column field="deleted_at" header="Deleted At" :sortable="true">
                <template #body="{data}">
                    {{ data.deleted_at ? $date(data.deleted_at).format("DD/MM/YYYY HH:mm") : null }}
                </template>
            </Column>
            <Column header="Actions" style="width: 0">
                <template #body="{data}">
                    <TableRecordActions
                        :name="Str.of(data.title).truncate(20).value"
                        :deleted="!!data.deleted_at"
                        :routes="{
                            show: {
                                name: 'admin.questions.show',
                                params: {question: data.id},
                            },
                            edit: {
                                name: 'admin.questions.edit',
                                params: {question: data.id},
                            },
                            delete: route('admin.questions.destroy', data.id),
                            restore: route('admin.questions.restore', data.id),
                        }"
                        @deleted="getQuestions"
                    />
                </template>
            </Column>
            <template #expansion="{data}">
                <pre>{{ data }}</pre>
            </template>
        </DataTable>
    </div>
</template>
<style scoped></style>
